(() => {
  const LOCK = 'salut-cart-recompute';
  const PENDING_KEY = 'salutCartRecomputePending';
  const PENDING_MAX_AGE = 10 * 60 * 1000;
  const IN_FLIGHT_GRACE = 3000;

  let localQueue = Promise.resolve();

  function withLock(task) {
    if (navigator.locks && navigator.locks.request) {
      return navigator.locks.request(LOCK, task);
    }
    const run = localQueue.then(task, task);
    localQueue = run.catch(() => {});
    return run;
  }

  const getCart = () => fetch('/cart.js?t=' + Date.now()).then((r) => r.json());

  // Line keys change when the cart is recalculated, so a line is tracked by its
  // variant and properties and its current key is looked up before every write.
  const lineId = (item) => item.variant_id + '|' + JSON.stringify(item.properties || {});
  const findLine = (cart, id) => (cart.items || []).find((item) => lineId(item) === id);

  async function setQuantity(id, quantity) {
    const line = findLine(await getCart(), id);
    if (!line) return;
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ updates: { [line.key]: quantity } }),
    });
  }

  function readPending() {
    try {
      const pending = JSON.parse(localStorage.getItem(PENDING_KEY) || 'null');
      if (pending && pending.id && Date.now() - pending.at < PENDING_MAX_AGE) return pending;
    } catch (e) {}
    clearPending();
    return null;
  }

  function writePending(pending) {
    try {
      localStorage.setItem(PENDING_KEY, JSON.stringify(pending));
    } catch (e) {}
  }

  function clearPending() {
    try {
      localStorage.removeItem(PENDING_KEY);
    } catch (e) {}
  }

  // Only restores a line still sitting at the temporary quantity, so a change the
  // customer made in the meantime is never overwritten. A page that unloads mid-run
  // releases the lock while its last write may still land, hence the grace wait.
  async function restoreInterrupted() {
    const pending = readPending();
    if (!pending) return false;
    const wait = pending.at + IN_FLIGHT_GRACE - Date.now();
    if (wait > 0) await new Promise((resolve) => setTimeout(resolve, wait));
    const line = findLine(await getCart(), pending.id);
    clearPending();
    if (!line || line.quantity !== pending.temporary) return false;
    await setQuantity(pending.id, pending.quantity);
    return true;
  }

  // Shopify re-evaluates automatic discounts only on a line change, so one line is
  // moved to a temporary quantity and back. Returns the recomputed cart.
  function run() {
    return withLock(async () => {
      await restoreInterrupted();
      let cart = await getCart();
      const line = (cart.items || [])[0];
      if (!line) return cart;

      const pending = {
        id: lineId(line),
        quantity: line.quantity,
        temporary: line.quantity > 1 ? line.quantity - 1 : line.quantity + 1,
        at: Date.now(),
      };
      writePending(pending);
      await setQuantity(pending.id, pending.temporary);
      await setQuantity(pending.id, pending.quantity);

      cart = await getCart();
      const restored = findLine(cart, pending.id);
      if (restored && restored.quantity !== pending.quantity) {
        await setQuantity(pending.id, pending.quantity);
        cart = await getCart();
      }
      clearPending();
      return cart;
    });
  }

  function restorePending() {
    if (!readPending()) return Promise.resolve(false);
    return withLock(restoreInterrupted);
  }

  window.SalutCartRecompute = { run, restorePending };
})();
